<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-3 sidebar-org bg-custom-blue py-3">

        <!-- Header left sidebar -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">{{ $t("views.nav.manageEmployees") }}</h4>
          <button v-if="isAdmin" @click="openModal(false, 'add')" type="button" class="btn btn-sm btn-primary" data-bs-toggle="modal" data-bs-target="#employeeEditModal">+ {{ $t("general.add") }}</button>
          <button v-if="!isAdmin" type="button" class="btn btn-sm btn-primary" disabled> {{ $t("general.add") }} </button>
        </div>

        <!-- Search & Checkbox -->
        <div class="row mt-4">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Suchfeld links -->
              <div class="me-3" style="width: 200px"><input v-model="filterName" type="text" class="form-control form-control-sm mt-0" :placeholder='$t("general.placeholderSearch")'></div>
              <!-- Checkbox Aktive Mitarbeiter -->
              <div class="col-6 my-auto">
                <div class="form-check float-end my-auto">
                  <input @change="toggleActiveCheckbox()" v-model="activeOnly" class="form-check-input" type="checkbox" value="activeOnly" id="activeOnly">
                  <label class="form-check-label" for="activeOnly">{{ $t("views.org.employee.onlyActive") }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <div v-for="employee in filteredEmployees" :key="employee.id" class="employee">
              <div class="py-2 border-bottom border-dark">
                <div class="cont-icon-employee d-inline-block me-1">
                  <div>
                    <svg v-if="employee.role === 10387" class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconAdmin'">
                      <use xlink:href="@/assets/icons/icons.svg#employee-admin"/>
                    </svg>
                    <svg v-else-if="employee.role === 15972" class="bi" width="16" height="16" fill="#6c80bb" v-tooltip="'tooltip.iconUser'">
                      <use xlink:href="@/assets/icons/icons.svg#employee-user"/>
                    </svg>
                    <svg v-else class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconMitarbeiter'">
                      <use xlink:href="@/assets/icons/icons.svg#employee-mitarbeiter"/>
                    </svg>
                  </div>
                </div>
                <div class="d-inline-block">
                  <span @click="selectEmployee(employee.id)" class="employee-name" :class="selectedEmployee.id === employee.id ? 'fw-bold' : ''"
                        role="button">{{ employee.lastName }}, {{ employee.firstName }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-9 px-5 py-3">
        <div v-if="Object.keys(selectedEmployee).length" class="row">
          <div class="col-12">
            <div class="row mt-3">
              <div class="col">
                <button v-if="isAdmin" @click="openModal(selectedEmployee.id, 'update')" type="button" class="btn btn-sm btn-outline-dark" data-bs-toggle="modal" data-bs-target="#employeeEditModal">&bull; {{ $t("general.update") }}</button>
                <button v-if="!isAdmin" type="button" class="btn btn-sm btn-outline-dark" disabled> {{ $t("general.update") }} </button>
              </div>
            </div>

            <!-- Name & Heading Data -->
            <div class="row mt-3">
              <div class="col">

                <div class="h4">{{ selectedEmployee.lastName }}, {{ selectedEmployee.firstName }}</div>

                <ul class="list-group list-group-horizontal-md foc-list-header">
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.org.employee.email") }}:</div>
                    <div>{{ selectedEmployee.email }}</div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.org.employee.activeFromTo") }}:</div>
                      <div>
                        <span v-if="selectedEmployee.validFrom"> {{ formatDate(new Date(selectedEmployee.validFrom)) }} - {{ formatDate(new Date(selectedEmployee.validTo)) }} </span>
                        <span v-else> {{ $t("views.strategy.structure.infinite") }} </span>
                      </div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.org.employee.role") }}:</div>
                    <span v-if="selectedEmployee.role === 10387">{{ $t("views.org.employee.admin") }}</span>
                    <span v-else-if="selectedEmployee.role === 15972">{{ $t("views.org.employee.standard") }}</span>
                    <span v-else>{{ $t("views.org.employee.noAccount") }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header fw-bold">{{ $t("views.org.employee.headingCard") }}</div>
                  <div class="card-body">

                    <!-- Register Tabs  -->
                    <ul class="nav nav-tabs border-bottom" role="tablist">
                      <li class="nav-item" role="presentation">
                        <button class="nav-link text-dark active" id="orgunits-tab" data-bs-toggle="tab" data-bs-target="#orgunits" type="button" role="tab" aria-controls="orgunits" aria-selected="false">{{ $t("views.org.employee.orgUnits") }} <span v-if="selectedEmployee.orgUnits.length" class="badge bg-primary">{{ selectedEmployee.orgUnits.length }}</span></button>
                      </li>
                      <li class="nav-item" role="presentation">
                        <button class="nav-link text-dark" id="measures-tab" data-bs-toggle="tab" data-bs-target="#measures" type="button" role="tab" aria-controls="measures" aria-selected="false">{{ $t("views.org.employee.measures") }} <span v-if="selectedEmployee.measures.length" class="badge bg-primary">{{ selectedEmployee.measures.length }}</span></button>
                      </li>
                      <li class="nav-item" role="presentation">
                        <button class="nav-link text-dark" id="responsibilities-tab" data-bs-toggle="tab" data-bs-target="#responsibilities" type="button" role="tab" aria-controls="responsibilities" aria-selected="false">{{ $t("views.org.employee.responsibilities") }} <span v-if="selectedEmployee.responsibilities.length" class="badge bg-primary">{{ selectedEmployee.responsibilities.length }}</span></button>
                      </li>
                    </ul>

                    <div class="tab-content mt-2">
                      <div class="tab-pane fade show active" id="orgunits" role="tabpanel">
                        <div v-if="selectedEmployee.orgUnits.length">
                          <table class="table">
                            <thead>
                              <tr class="border-dark">
                                <th scope="col">{{ $t("views.org.edit.name") }}</th>
                                <th scope="col">{{ $t("general.role") }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="orgUnit in selectedEmployee.orgUnits" :key="'orgunit' + orgUnit.id">
                                <td>
                                  <div class="cont-icon-org-unit d-inline-block me-1">
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconOrgUnit'"> <use xlink:href="@/assets/icons/icons.svg#orgunit" /> </svg> &nbsp;
                                    <a @click="selectOrgUnit(orgUnit.id)" role="button" scope="row" class="fw-normal">{{ orgUnit.name }}</a>
                                  </div>
                                </td>
                                <td scope="row" class="fw-normal">{{ orgUnit.role }}</td>
                                <td>&nbsp;</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="mt-3 h6">{{ $t("views.org.employee.noOrgUnits") }}</div>
                      </div>
                      <div class="tab-pane fade" id="measures" role="tabpanel">
                        <div v-if="selectedEmployee.measures.length">
                          <table class="table">
                            <thead>
                              <tr class="border-dark">
                                <th scope="col">{{ $t("views.org.employee.measureTitle") }}</th>
                                <th scope="col">{{ $t("views.org.employee.measureConversion") }}</th>
                                <th scope="col">{{ $t("views.org.employee.measureValidFrom") }}</th>
                                <th scope="col">{{ $t("views.org.employee.measureValidTo") }}</th>
                                <th scope="col">{{ $t("views.org.employee.measureRelatedObject") }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="measure in selectedEmployee.measures" :key="'measure' + measure.id">
                                <th class="fw-normal" v-tooltip.bottom="{ key: 'tooltip.preview', params: { html: measure.measure } }">{{ measure.title }}</th>
                                <td>
                                  <div class="progress position-relative border bg-dark border-dark w-50">
                                    <span class="progress-label position-absolute start-50 top-50 text-light fw-bold">{{ 25 * measure.conversion }}%</span>
                                    <div class="progress-bar progress-bar-striped bg-primary" :style="'width: ' + (25 * measure.conversion) + '%'" role="progressbar" :aria-valuenow="(25 * measure.conversion)" aria-valuemin="0" aria-valuemax="100"></div>
                                  </div>
                                </td>
                                <td>{{ formatDate(new Date(measure.validFrom)) }}</td>
                                <td :class="new Date(measure.validTo) < new Date() ? 'text-danger' : 'text-dark'">{{ formatDate(new Date(measure.validTo)) }}</td>
                                <td>
                                  <div v-if="measure.type === 'objective'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconObjective'"> <use xlink:href="@/assets/icons/icons.svg#objective" /> </svg> &nbsp;
                                    <a :href="createLink('objective', measure.parentObjectUuid)" target="_blank"> {{ measure.category === 1 ? measure.name.toUpperCase() : measure.name }} </a>
                                  </div>
                                  <div v-if="measure.type === 'project'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconProject'"> <use xlink:href="@/assets/icons/icons.svg#project" /> </svg> &nbsp;
                                    <a :href="createLink('project', measure.parentObjectUuid)" target="_blank"> {{ measure.name }} </a>
                                  </div>
                                  <div v-if="measure.type === 'strategy'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconStrategy'"> <use xlink:href="@/assets/icons/icons.svg#strategy" /> </svg> &nbsp;
                                    <a :href="createLink('strategy', measure.parentObjectUuid)" target="_blank"> {{ measure.name }} </a>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="mt-3 h6">{{ $t("views.org.employee.emptyMeasures") }}</div>
                      </div>
                      <div class="tab-pane fade" id="responsibilities" role="tabpanel">
                        <div v-if="selectedEmployee.responsibilities.length">
                          <table class="table">
                            <thead>
                              <tr class="border-dark">
                                <th scope="col">{{ $t("views.org.employee.responsibilityRelatedObject") }}</th>
                                <th scope="col">{{ $t("views.org.employee.responsibilityValidFrom") }}</th>
                                <th scope="col">{{ $t("views.org.employee.responsibilityValidTo") }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="resp in selectedEmployee.responsibilities" :key="'resp' + resp.id">
                                <td>
                                  <div v-if="resp.type === 'orgunit'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconOrgUnit'"> <use xlink:href="@/assets/icons/icons.svg#orgunit" /> </svg> &nbsp;
                                    <a :href="createLink('orgunit', resp.parentObjectUuid)" target="_blank"> {{ resp.name }} </a>
                                  </div>
                                  <div v-if="resp.type === 'strategy'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconStrategy'"> <use xlink:href="@/assets/icons/icons.svg#strategy" /> </svg> &nbsp;
                                    <a :href="createLink('strategy', resp.parentObjectUuid)" target="_blank"> {{ resp.name }} </a>
                                  </div>
                                  <div v-if="resp.type === 'project'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconProject'"> <use xlink:href="@/assets/icons/icons.svg#project" /> </svg> &nbsp;
                                    <a :href="createLink('project', resp.parentObjectUuid)" target="_blank"> {{ resp.category === 1 ? resp.name.toUpperCase() : resp.name }} </a>
                                  </div>
                                  <div v-if="resp.type === 'objective'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconObjective'"> <use xlink:href="@/assets/icons/icons.svg#objective" /> </svg> &nbsp;
                                    <a :href="createLink('objective', resp.parentObjectUuid)" target="_blank"> {{ resp.category === 1 ? resp.name.toUpperCase() : resp.name }} </a>
                                  </div>
                                  <div v-if="resp.type === 'kpi'" >
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconKpi'"> <use xlink:href="@/assets/icons/icons.svg#kpi" /> </svg> &nbsp;
                                    <a :href="createLink('kpi', resp.parentObjectUuid)" target="_blank"> {{ resp.name }} </a>
                                  </div>
                                </td>
                                <td>{{ formatDate(new Date(resp.validFrom)) }}</td>
                                <td>{{ formatDate(new Date(resp.validTo)) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="mt-3 h6">{{ $t("views.org.employee.emptyResponsibilities") }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center mt-5">
          <img src="@/assets/img/no_data.svg" class="img-fluid" width="250">
          <p class="h5 mt-3">{{ $t("views.org.employee.helpChooseEmployee") }}</p>
        </div>
      </div>
    </div>
    <div ref="employeeEditModal" class="modal fade" data-bs-backdrop="static" id="employeeEditModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" v-draggable-modal>
        <div class="modal-content bg-primary">
          <div class="modal-header">
            <div class="h5 modal-title text-light">{{ $t("views.org.employee.headingEditEmployee") }} </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body bg-light">
            <div class="row mb-3">
              <div class="col">
                <label for="role" class="form-label">{{ $t("views.org.employee.role") }}</label>
                <select v-model="form.employee.role.content" id="role" class="form-select">
                  <option value="13512">{{ $t("views.org.employee.noAccount") }}</option>
                  <option value="15972">{{ $t("views.org.employee.standard") }}</option>
                  <option value="10387">{{ $t("views.org.employee.admin") }}</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <label for="firstName" class="form-label">{{ $t("views.org.employee.firstName") }}<span class="text-danger">*</span></label>
                <input v-model="form.employee.firstName.content" id="firstName" :class="form.employee.firstName.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.employee.firstName.error }}</div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <label for="lastName" class="form-label">{{ $t("views.org.employee.lastName") }}<span class="text-danger">*</span></label>
                <input v-model="form.employee.lastName.content" id="lastName" :class="form.employee.lastName.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.employee.lastName.error }}</div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <label for="email" class="form-label">{{ $t("views.org.employee.email") }}<span class="text-danger">*</span></label>
                <input v-model="form.employee.email.content" type="email" id="email" :class="form.employee.email.error === '' ? 'form-control' : 'form-control is-invalid'"  :disabled="$store.state.selectedEmployee.emailChangeable === false && mode === 'update' ? true : false">
                <div class="invalid-feedback">{{ form.employee.email.error }}</div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-6">
                <label for="employeeValidFrom" class="form-label">{{ $t("views.org.employee.activeFrom") }}</label>
                <input v-model="form.employee.validFrom.content" type="date" id="employeeValidFrom" :class="form.employee.validFrom.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.employee.validFrom.error }}</div>
              </div>
              <div class="col-6">
                <label for="employeeValidTo" class="form-label">{{ $t("views.org.employee.activeTo") }}</label>
                <input v-model="form.employee.validTo.content" type="date" id="employeeValidTo" :class="form.employee.validTo.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.employee.validTo.error }}</div>
              </div>
            </div>
          </div>
          <div v-if="dialogInfo || dialogError" class="modal-footer bg-light justify-content-between">
            <label class="form-label small" :class="dialogInfo ? 'text-info-emphasis' : 'text-danger'">{{ $store.state.dialogMessage }}</label>
          </div>
          <div class="modal-footer bg-light justify-content-between">
            <div>
              <button v-if="mode != 'add'" @click="checkDeleteEmployee()" type="button" class="btn btn-sm btn-outline-danger" >{{ $t("general.delete") }}</button>
            </div>
            <div>
              <button @click="cancel()" type="button" class="btn btn-sm btn-outline-secondary me-2" data-bs-dismiss="modal">{{ $t("general.close") }}</button>
              <button @click="save()" type="button" class="btn btn-sm btn-primary">{{ $t("general.save") }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal fade" data-bs-backdrop="static" id="employeeDeleteModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" v-draggable-modal>
        <div class="modal-content bg-primary">
          <div class="modal-header">
            <div class="h5 modal-title text-light">{{ $t("views.org.employee.headingDeleteEmployee") }}</div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body bg-light">
            <div class="text-center my-3">
              <p class="fw-bold">{{ form.employee.lastName.content }}, {{form.employee.firstName.content }}</p>
              <p>{{ $t("views.org.employee.confirmDeleteEmployee") }}</p>
            </div>
          </div>
          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">{{ $t("general.close") }}</button>
            <button @click="deleteEmployee()" type="button" class="btn btn-sm btn-danger" data-bs-dismiss="modal">{{ $t("general.delete") }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Modal } from 'bootstrap'
import initializeForm from '@/composables/initializeForm'
import * as validation from '@/composables/validation'
import * as formatUtils from '@/composables/formatUtils'

/* ---------------------------------------------
   Setup
--------------------------------------------- */
const store = useStore()
const router = useRouter()
const { t } = useI18n()

/* ---------------------------------------------
   Reactive State (früher: data())
--------------------------------------------- */
const mode = ref('add')
const dialogInfo = ref(false)
const dialogError = ref(false)
const activeOnly = ref(true)
const filterName = ref('')

const form = reactive({
  employee: {
    id: { content: '', error: '', validate: [] },
    role: { content: '', error: '', validate: [] },
    firstName: { content: '', error: '', maxLength: 150, validate: ['empty', 'length'] },
    lastName: { content: '', error: '', maxLength: 150, validate: ['empty', 'length'] },
    email: { content: '', error: '', validate: ['empty', 'email'] },
    emailOld: { content: '', error: '', validate: [] },
    validFrom: { content: '', error: '', validate: ['validFrom'] },
    validTo: { content: '', error: '', validate: ['validTo'] }
  }
})

/* ---------------------------------------------
   Computed
--------------------------------------------- */
const employees = computed(() => store.state.employees)

const filteredEmployees = computed(() =>
  employees.value.filter(emp =>
    emp.lastName.toLowerCase().includes(filterName.value.toLowerCase())
  )
)

const selectedEmployee = computed(() => store.state.selectedEmployee)
const isAdmin = computed(() => store.state.user.role === 10387)

/* ---------------------------------------------
   Modal Handling
--------------------------------------------- */
const employeeEditModal = ref(null)

const closeModal = () => {
  const instance = Modal.getInstance(employeeEditModal.value)
  instance?.hide()
}

/* ---------------------------------------------
   Methods
--------------------------------------------- */
function cancel () {
  dialogInfo.value = false
  dialogError.value = false
  initializeForm(form.employee)
}

function openModal (id, newMode) {
  dialogInfo.value = false
  dialogError.value = false
  store.state.dialogMessage = ''

  if (id) {
    Object.keys(form.employee).forEach(field => {
      if (field in selectedEmployee.value) {
        form.employee[field].content = selectedEmployee.value[field]
        form.employee[field].error = ''
      }
    })
    form.employee.emailOld.content = selectedEmployee.value.email
  }

  mode.value = newMode

  if (newMode === 'add') {
    form.employee.role.content = '13512'
  } else {
    const userLocale = store.state.user.settings?.language
    if (selectedEmployee.value.emailChangeable === false) {
      store.state.dialogMessage = t('error.2050', userLocale)
      dialogInfo.value = true
    }
    // Email of the current user can only be changed via account settings
    if (selectedEmployee.value.email === store.state.user.email) {
      store.state.dialogMessage = t('error.2051', userLocale)
      dialogError.value = true
    }
  }
}

function createLink (type, uuid) {
  const map = {
    orgunit: '/organization/manage/org-units?id=',
    objective: '/objectives/manage/objectives?id=',
    project: '/projects/manage/projects?id=',
    kpi: '/monitoring/manage/kpis?id=',
    strategy: '/strategies/manage/strategies?id='
  }
  return map[type] + uuid
}

function formatDate (date) {
  const userLocale = store.state.user.settings?.language
  return formatUtils.formatDate(userLocale, date)
}

function checkDeleteEmployee () {
  dialogInfo.value = false
  dialogError.value = false

  const userLocale = store.state.user.settings?.language

  if (form.employee.validFrom.content < form.employee.validTo.content) {
    store.state.dialogMessage = t('error.2046', userLocale)
    dialogError.value = true
  }
  if (!dialogError.value && selectedEmployee.value.email === store.state.user.email) {
    store.state.dialogMessage = t('error.2049', userLocale)
    dialogError.value = true
  }
  if (!dialogError.value && selectedEmployee.value.numberOfResponsibilities > 0) {
    store.state.dialogMessage = t('error.2047', userLocale)
    dialogError.value = true
  }
  if (!dialogError.value) {
    let modal = document.getElementById('employeeEditModal')
    Modal.getInstance(modal)?.hide()

    modal = new Modal(document.getElementById('employeeDeleteModal'))
    modal.show()
    return true
  }
}

async function selectEmployee (employeeId) {
  store.commit('SET_LOADING_STATUS', true)
  await store.dispatch('getEmployee', employeeId)
  await store.dispatch('getMyTasks', employeeId)
  store.commit('SET_LOADING_STATUS', false)
  window.scrollTo(0, 0)
}

async function selectOrgUnit (orgUnitId) {
  store.commit('SET_LOADING_STATUS', true)
  await store.dispatch('getOrgUnit', orgUnitId)
  router.push({ name: 'OrgManageOrgUnit' })
  store.commit('SET_LOADING_STATUS', false)
}

async function save () {
  store.commit('SET_LOADING_STATUS', true)
  store.state.dialogMessage = ''

  const valid = validation.fieldValidation(form.employee)
  if (valid === 0) {
    const dispatchFunc = mode.value + 'Employee'
    const employee = {}

    Object.keys(form.employee).forEach(field => {
      employee[field] = form.employee[field].content
    })

    await store.dispatch(dispatchFunc, employee)

    if (store.state.dialogMessage === '') {
      closeModal()
      Object.keys(form.employee).forEach(field => {
        form.employee[field].content = ''
        form.employee[field].error = ''
      })
    } else {
      dialogError.value = true
    }
  }

  store.commit('SET_LOADING_STATUS', false)

  if (store.state.dialogMessage === '') {
    await store.dispatch('getEmployees', { mode: activeOnly.value ? 'default' : 'all' })
  }
}

async function deleteEmployee () {
  store.commit('SET_LOADING_STATUS', true)
  await store.dispatch('deleteEmployee', form.employee.id.content)
  store.commit('SET_SELECTED_EMPLOYEE', {})
  await store.dispatch('getEmployees', { mode: activeOnly.value ? 'default' : 'all' })
  store.commit('SET_LOADING_STATUS', false)
}

async function toggleActiveCheckbox () {
  store.commit('SET_LOADING_STATUS', true)
  await store.dispatch('getEmployees', { mode: activeOnly.value ? 'default' : 'all' })
  store.commit('SET_LOADING_STATUS', false)
}

/* ---------------------------------------------
   Lifecycle (früher: created)
--------------------------------------------- */
onMounted(async () => {
  store.commit('SET_LOADING_STATUS', true)
  await store.dispatch('getEmployees', { mode: activeOnly.value ? 'default' : 'all' })
  store.commit('SET_LOADING_STATUS', false)

  if (Object.keys(selectedEmployee.value).length) {
    await store.dispatch('getEmployee', selectedEmployee.value.id)
  }
})

/* ---------------------------------------------
   Watchers
--------------------------------------------- */
watch(mode, newMode => {
  if (newMode === 'add') {
    Object.keys(form.employee).forEach(field => {
      form.employee[field].content = ''
      form.employee[field].error = ''
    })
    form.employee.role.content = '13512'
  }
})
</script>

<style lang="scss" scoped>
.employee {
  .cont-icon-employee span {
    font-size: 14px;
  }
  .employee-name:hover {
    font-weight: bold;
  }
}
</style>

<style lang="scss" scoped>
.progress-label {
  transform: translate(-50%, -50%);
}
.tab-content .progress {
  margin-top: 6px;
}
</style>
